import { parse as djotParse, type Doc } from '@djot/djot';
import { expandWikiLinks } from '@/lib/wikiLinkSyntax';

/**
 * Parses Djot markup into an AST. Never throws — falls back to an empty doc
 * on parse errors.
 *
 * Before parsing, classic double-bracket wikilinks (`[[Article]]`,
 * `[[Article|Display text]]`) are expanded into their spec-compliant NIP-54
 * Djot equivalents (`[Article][]`, `[Display text][Article]`) — see
 * `@/lib/wikiLinkSyntax` for the rationale. This means both the live editor
 * preview and the published-article view render `[[...]]` wikilinks
 * identically, regardless of whether the underlying `content` string has
 * already been expanded (e.g. articles fetched from relays, which were
 * expanded at publish time) or still contains the raw shorthand (e.g. the
 * text currently being typed in the editor).
 */
export function parseDjot(content: string): Doc {
  try {
    return djotParse(expandWikiLinks(content), { sourcePositions: false });
  } catch (error) {
    // Logged (not swallowed silently) so a bad Djot parse is debuggable —
    // callers should treat an empty result as a signal to fall back to
    // another renderer rather than showing nothing (see `WikiContent`'s
    // `isEmptyParse` safety net).
    console.warn('Failed to parse Djot content, rendering as empty:', error);
    return {
      tag: 'doc',
      references: {},
      autoReferences: {},
      footnotes: {},
      children: [],
    };
  }
}
